import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CategoryHero = ({ selectedCategory, onCategoryChange, productCount }) => {
  const categories = [
    {
      id: 'all',
      name: 'All Products',
      icon: 'Grid3X3',
      description: 'Browse our complete electronics collection',
      count: 1012,
      gradient: 'from-primary/20 to-secondary/20'
    },
    {
      id: 'phones',
      name: 'Smartphones',
      icon: 'Smartphone',
      description: 'Latest smartphones and mobile devices',
      count: 245,
      gradient: 'from-blue-500/20 to-purple-500/20'
    },
    {
      id: 'laptops',
      name: 'Laptops',
      icon: 'Laptop',
      description: 'High-performance laptops and notebooks',
      count: 189,
      gradient: 'from-green-500/20 to-blue-500/20'
    },
    {
      id: 'chargers',
      name: 'Chargers',
      icon: 'Zap',
      description: 'Power adapters and charging solutions',
      count: 156,
      gradient: 'from-yellow-500/20 to-orange-500/20'
    },
    {
      id: 'accessories',
      name: 'Accessories',
      icon: 'Headphones',
      description: 'Tech accessories and peripherals',
      count: 324,
      gradient: 'from-purple-500/20 to-pink-500/20'
    },
    {
      id: 'airpods',
      name: 'AirPods & Earbuds',
      icon: 'Volume2',
      description: 'Wireless earbuds and audio devices',
      count: 98,
      gradient: 'from-red-500/20 to-orange-500/20'
    }
  ];

  const currentCategory = categories?.find(cat => cat?.id === selectedCategory) || categories?.[0];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-background via-muted/10 to-background rounded-lg border border-border">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      <div className="relative p-8">
        {/* Main Hero Content */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${currentCategory?.gradient} mb-4`}>
              <Icon name={currentCategory?.icon} size={32} className="text-foreground" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">
              {currentCategory?.name}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {currentCategory?.description}
            </p>
            
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Package" size={16} />
                <span>{productCount || currentCategory?.count} Products</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Truck" size={16} />
                <span>Free Shipping</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} />
                <span>Warranty Included</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Category Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3"
        >
          {categories?.map((category) => (
            <Button
              key={category?.id}
              variant={selectedCategory === category?.id ? "default" : "outline"}
              onClick={() => onCategoryChange(category?.id)}
              className={`neural-nav-item ${
                selectedCategory === category?.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name={category?.icon} size={18} />
                <span className="font-medium">{category?.name}</span>
                <span className="text-xs opacity-75">({category?.count})</span>
              </div>
            </Button>
          ))}
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
        >
          <div className="text-center p-4 bg-card/50 rounded-lg border border-border/50">
            <div className="text-2xl font-bold text-primary mb-1">24/7</div>
            <div className="text-xs text-muted-foreground">Support</div>
          </div>
          <div className="text-center p-4 bg-card/50 rounded-lg border border-border/50">
            <div className="text-2xl font-bold text-success mb-1">4.8★</div>
            <div className="text-xs text-muted-foreground">Rating</div>
          </div>
          <div className="text-center p-4 bg-card/50 rounded-lg border border-border/50">
            <div className="text-2xl font-bold text-warning mb-1">1M+</div>
            <div className="text-xs text-muted-foreground">Customers</div>
          </div>
          <div className="text-center p-4 bg-card/50 rounded-lg border border-border/50">
            <div className="text-2xl font-bold text-secondary mb-1">99%</div>
            <div className="text-xs text-muted-foreground">Satisfaction</div>
          </div>
        </motion.div>
      </div>
      {/* Data Stream Animation */}
      <div className="data-stream"></div>
    </div>
  );
};

export default CategoryHero;